/**
 * ProgressMomentumBand.jsx — Dashboard "orienté progrès" (Phase 4 Engagement)
 * ===========================================================================
 * Bandeau de progression en tête du cockpit. Recadre l'expérience autour du
 * PROGRÈS (maîtrise · régularité · élan vs soi-même) plutôt que des chiffres de
 * contrôle. Données dérivées des actions réelles (GET /engagement/progress).
 * Premium, sobre — aucune ludification enfantine.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../../lib/api';
import { Award, CalendarCheck, TrendingUp, Minus } from 'lucide-react';

export default function ProgressMomentumBand() {
    const [data, setData] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get('/engagement/progress');
                if (alive) setData(r.data);
            } catch (e) {
                // silencieux
            }
        })();
        return () => { alive = false; };
    }, []);

    if (!data) return null;

    const maitrise = data.maitrise || {};
    const regularite = data.regularite || {};
    const elan = data.elan || {};
    const delta = elan.delta ?? 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-border bg-card/70 p-3 sm:p-4 mb-4"
            data-testid="progress-momentum-band"
        >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:divide-x sm:divide-white/5">
                {/* Maîtrise */}
                <div className="sm:pr-4" data-testid="momentum-mastery">
                    <div className="flex items-center gap-1.5 mb-1.5">
                        <Award className="w-3.5 h-3.5 text-[#A855F7]" />
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Maîtrise</span>
                    </div>
                    <p className="text-sm font-bold text-foreground truncate">{maitrise.title}</p>
                    <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <motion.div
                            className="h-full rounded-full bg-gradient-to-r from-[#681793] to-[#A855F7]"
                            initial={{ width: 0 }}
                            animate={{ width: `${maitrise.progress_percent || 0}%` }}
                            transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                        />
                    </div>
                    {maitrise.next_level_title && (
                        <p className="text-[10px] text-muted-foreground mt-1">→ {maitrise.next_level_title}</p>
                    )}
                </div>

                {/* Régularité */}
                <div className="sm:px-4" data-testid="momentum-regularity">
                    <div className="flex items-center gap-1.5 mb-1.5">
                        <CalendarCheck className="w-3.5 h-3.5 text-[#FF914D]" />
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Régularité</span>
                    </div>
                    <p className="text-2xl font-black text-foreground leading-none">
                        {regularite.current_streak}<span className="text-sm font-medium text-muted-foreground ml-1">j</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1.5">Meilleure série : {regularite.longest_streak} j</p>
                </div>

                {/* Élan (vs soi-même) */}
                <div className="sm:pl-4" data-testid="momentum-elan">
                    <div className="flex items-center gap-1.5 mb-1.5">
                        <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Élan cette semaine</span>
                    </div>
                    <p className="text-2xl font-black text-foreground leading-none">{elan.this_week ?? 0}</p>
                    <p className="text-[10px] mt-1.5 flex items-center gap-1">
                        {delta > 0 ? (
                            <span className="text-emerald-400 flex items-center gap-1">
                                <TrendingUp className="w-3 h-3" /> +{delta} vs semaine dernière
                            </span>
                        ) : (
                            <span className="text-muted-foreground flex items-center gap-1">
                                <Minus className="w-3 h-3" /> {elan.last_week ?? 0} la semaine dernière
                            </span>
                        )}
                    </p>
                </div>
            </div>
        </motion.div>
    );
}
